/**
 * File helper utilities for downloading files and generating PDFs
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Returns true if the stored value is a disk-path key (new storage),
 * false if it is a legacy base64 data URI.
 */
export const isFileKey = (value) =>
  Boolean(value) && !value.startsWith('data:');

/**
 * Trigger a browser download from a blob.
 */
const triggerBlobDownload = (blob, filename) => {
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href     = url;
  link.download = filename || 'download';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Downloads a file — handles both legacy base64 data URIs and new disk-path keys.
 * Returns true on success, false on failure.
 */
export const downloadFileWithCorrectType = async (dataOrKey, filename, mimeType) => {
  try {
    if (!dataOrKey) return false;

    // ── New: disk-path key → fetch from backend with auth ──────────────────
    if (isFileKey(dataOrKey)) {
      const token = localStorage.getItem('token');
      const url   = `${BACKEND_URL}/api/${dataOrKey}`;
      const res   = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return false;
      const blob  = await res.blob();
      triggerBlobDownload(blob, filename);
      return true;
    }

    // ── Legacy: base64 data URI ─────────────────────────────────────────────
    let base64Content   = dataOrKey;
    let detectedMime    = mimeType;

    if (dataOrKey.startsWith('data:')) {
      const matches = dataOrKey.match(/^data:([^;]+);base64,(.+)$/);
      if (matches) {
        detectedMime   = matches[1];
        base64Content  = matches[2];
      } else {
        const parts = dataOrKey.split(',');
        if (parts.length > 1) {
          base64Content = parts[1];
          const m = parts[0].match(/^data:([^;]+)/);
          if (m) detectedMime = m[1];
        }
      }
    }

    const finalMime   = detectedMime || mimeType || 'application/octet-stream';
    const binary      = atob(base64Content);
    const bytes       = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const blob        = new Blob([bytes], { type: finalMime });
    triggerBlobDownload(blob, filename);
    return true;
  } catch (error) {
    console.error('Download failed:', error);
    return false;
  }
};

/**
 * Fetches a file by disk-path key and returns an object URL suitable for
 * use in <img src> or <iframe src>. Caller must revoke the URL when done.
 * Returns null on failure.
 */
export const fetchFileAsObjectUrl = async (fileKey) => {
  try {
    if (!fileKey || !isFileKey(fileKey)) return null;
    const token = localStorage.getItem('token');
    const res   = await fetch(`${BACKEND_URL}/api/${fileKey}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
};

// ─── Design tokens ────────────────────────────────────────────────────────────
// All as plain [r, g, b] arrays — jspdf-autotable requires arrays, not objects.
const NAVY    = [15,  23,  42];
const RED     = [177, 31,  36];
const WHITE   = [255, 255, 255];
const GREY50  = [249, 250, 251];
const GREY100 = [243, 244, 246];
const GREY200 = [229, 231, 235];
const GREY500 = [107, 114, 128];
const GREY700 = [55,  65,  81];
const GREY900 = [17,  24,  39];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (v) => (v == null || v === '' ? 'N/A' : String(v));

const calcAge = (dob) => {
  if (!dob) return null;
  try {
    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age >= 0 ? age : null;
  } catch { return null; }
};

const fmtDateTime = (iso) => {
  if (!iso) return 'N/A';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return String(iso); }
};

/**
 * Generates a PDF application record suitable for sharing with the applicant.
 * Does NOT include any admin-computed data (scores, eligibility, review notes).
 */
export const generateApplicationPDFWithLibrary = async (application) => {
  const { jsPDF }              = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const doc   = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();   // 210
  const pageH = doc.internal.pageSize.getHeight();  // 297
  const mL    = 14;
  const mR    = 14;
  const cW    = pageW - mL - mR;  // 182

  // ── Parse nested JSON fields ──────────────────────────────────────────────
  const extra = (() => { try { return JSON.parse(application.additional_qualifications); } catch { return {}; } })();
  const pd    = extra.personal_details || {};
  const oLvl  = extra.o_levels  || {};
  const aLvl  = extra.a_levels  || {};
  const llb   = extra.llb       || {};
  const dynDecl = extra.declaration_responses || {};  // dynamic declaration responses

  let y = 0;

  // ── Section heading: light grey bar with red left accent ──────────────────
  const sectionHeading = (title) => {
    if (y + 14 > pageH - 22) { doc.addPage(); y = 20; }
    doc.setFillColor(...GREY100);
    doc.rect(mL, y, cW, 8, 'F');
    doc.setFillColor(...RED);
    doc.rect(mL, y, 2.5, 8, 'F');
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...GREY900);
    doc.text(title.toUpperCase(), mL + 6, y + 5.4);
    y += 11;
  };

  // ── Two-column key/value table ────────────────────────────────────────────
  const dataTable = (rows) => {
    if (!rows.length) return;
    autoTable(doc, {
      startY: y,
      margin: { left: mL, right: mR },
      tableWidth: cW,
      head: [],
      body: rows,
      theme: 'plain',
      styles: {
        fontSize: 9,
        cellPadding: { top: 2.5, bottom: 2.5, left: 4, right: 4 },
        valign: 'top',
        overflow: 'linebreak',
        textColor: GREY900,
        lineColor: GREY200,
        lineWidth: 0.2,
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 52, fillColor: GREY50, textColor: GREY700 },
        1: { cellWidth: cW - 52 },
      },
      alternateRowStyles: { fillColor: WHITE },
      rowPageBreak: 'avoid',
    });
    y = doc.lastAutoTable.finalY + 5;
  };

  // ── Page numbers on every page (called after all content) ─────────────────
  const addPageNumbers = () => {
    const total = doc.getNumberOfPages();
    for (let i = 1; i <= total; i++) {
      doc.setPage(i);
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...GREY500);
      doc.setDrawColor(...GREY200);
      doc.setLineWidth(0.3);
      doc.line(mL, pageH - 11, pageW - mR, pageH - 11);
      doc.text(`Page ${i} of ${total}`, pageW - mR, pageH - 7, { align: 'right' });
      doc.text('FB Attorneys Academy', mL, pageH - 7);
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  //  HEADER
  // ══════════════════════════════════════════════════════════════════════════
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pageW, 32, 'F');
  doc.setFillColor(...RED);
  doc.rect(0, 32, pageW, 2.5, 'F');

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...WHITE);
  doc.text('FB ATTORNEYS ACADEMY', mL, 13);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(180, 190, 210);
  doc.text('Application Record', mL, 21);

  doc.setFontSize(7.5);
  doc.setTextColor(160, 175, 200);
  doc.text(`Ref: ${application.id.substring(0, 8).toUpperCase()}`, pageW - mR, 13, { align: 'right' });
  doc.text(`Generated: ${fmtDateTime(new Date().toISOString())}`, pageW - mR, 20, { align: 'right' });

  y = 42;

  // ── Applicant name ────────────────────────────────────────────────────────
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...NAVY);
  doc.text(fmt(application.full_name), mL, y);

  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GREY500);
  doc.text(`Submitted: ${fmtDateTime(application.submitted_at)}`, mL, y + 6);

  y += 14;

  doc.setDrawColor(...GREY200);
  doc.setLineWidth(0.3);
  doc.line(mL, y, pageW - mR, y);
  y += 6;

  // ══════════════════════════════════════════════════════════════════════════
  //  1. PERSONAL INFORMATION
  // ══════════════════════════════════════════════════════════════════════════
  sectionHeading('1. Personal Information');
  dataTable([
    ['Last Name',           fmt(pd.last_name)],
    ['First Name',          fmt(pd.first_name)],
    ['Middle Name',         fmt(pd.middle_name)],
    ['NIDA / Passport No.', fmt(pd.nida_passport)],
    ['ID Expiry Date',      fmt(pd.id_expiry)],
    ['Date of Birth',       fmt(application.date_of_birth)],
    ['Age',                 (() => { const a = extra.age ?? calcAge(application.date_of_birth); return a != null ? `${a} years` : 'N/A'; })()],
    ['Place of Birth',      fmt(pd.birth_place)],
    ['Email',               fmt(application.email)],
    ['Mobile Number',       fmt(application.phone)],
    ['WhatsApp Number',     pd.whatsapp ? fmt(pd.whatsapp) : 'Same as mobile'],
    ['Residential Address', fmt(application.address)],
    ['Currently Employed / Intern',
      application.currently_employed === true
        ? (application.employer_name ? `Yes — ${application.employer_name}` : 'Yes')
        : application.currently_employed === false
          ? 'No'
          : 'Not answered'],
  ]);

  // ══════════════════════════════════════════════════════════════════════════
  //  2. O-LEVEL RESULTS
  // ══════════════════════════════════════════════════════════════════════════
  sectionHeading('2. O-Level Results');
  dataTable([
    ['Year of Graduation', fmt(oLvl.year)],
    ['School Name',        fmt(oLvl.school)],
    ['Division',           fmt(oLvl.division)],
  ]);

  // ══════════════════════════════════════════════════════════════════════════
  //  3. A-LEVEL / ADVANCED EDUCATION
  // ══════════════════════════════════════════════════════════════════════════
  sectionHeading('3. A-Level / Advanced Education');
  dataTable([
    ['Year of Graduation', fmt(aLvl.year)],
    ['School Name',        fmt(aLvl.school)],
    ['Division',           fmt(aLvl.division || application.a_level_division)],
    ...(aLvl.no_a_level_details
      ? [['Alternative Qualification', fmt(aLvl.no_a_level_details)]]
      : []),
  ]);

  const subjects = (aLvl.subjects || []).filter(s => s && s.subject);
  if (subjects.length > 0) {
    if (y + 10 > pageH - 22) { doc.addPage(); y = 20; }
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...GREY700);
    doc.text('Subjects & Grades', mL + 4, y + 1);
    y += 4;
    autoTable(doc, {
      startY: y,
      margin: { left: mL + 52, right: mR },
      tableWidth: cW - 52,
      head: [['Subject', 'Grade']],
      body: subjects.map(s => [fmt(s.subject), fmt(s.grade)]),
      theme: 'grid',
      headStyles: { fillColor: GREY700, textColor: WHITE, fontSize: 8, fontStyle: 'bold', cellPadding: 2.5 },
      styles: { fontSize: 8.5, cellPadding: 2.5, textColor: GREY900, lineColor: GREY200, lineWidth: 0.2 },
      alternateRowStyles: { fillColor: GREY50 },
      rowPageBreak: 'avoid',
    });
    y = doc.lastAutoTable.finalY + 5;
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  4. LLB DETAILS
  // ══════════════════════════════════════════════════════════════════════════
  sectionHeading('4. LLB Details');
  dataTable([
    ['Graduation Date', fmt(llb.graduation_date)],
    ['Class / Honours', fmt(llb.class)],
    ['CGPA',            application.llb_gpa != null ? String(application.llb_gpa) : 'N/A'],
    ['Institute',       fmt(llb.institute)],
    ['Years Attended',  fmt(llb.years_attended)],
  ]);

  // ══════════════════════════════════════════════════════════════════════════
  //  5. PERSONAL STATEMENT
  // ══════════════════════════════════════════════════════════════════════════
  sectionHeading('5. Personal Statement');
  const statement = extra.personal_statement || 'No personal statement provided.';
  const stLines   = doc.splitTextToSize(statement, cW - 12);
  const stH       = stLines.length * 4.6 + 8;
  if (y + stH > pageH - 22) { doc.addPage(); y = 20; }
  doc.setFillColor(...GREY50);
  doc.setDrawColor(...GREY200);
  doc.setLineWidth(0.3);
  doc.rect(mL, y, cW, stH, 'FD');
  doc.setFillColor(...RED);
  doc.rect(mL, y, 2.5, stH, 'F');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GREY900);
  doc.text(stLines, mL + 6, y + 5, { lineHeightFactor: 1.4 });
  y += stH + 5;

  // ══════════════════════════════════════════════════════════════════════════
  //  6. REFEREES
  // ══════════════════════════════════════════════════════════════════════════
  sectionHeading('6. Referees');
  const labelW = 24;
  const valW   = (cW / 2) - labelW;

  autoTable(doc, {
    startY: y,
    margin: { left: mL, right: mR },
    tableWidth: cW,
    head: [[
      { content: 'Referee 1', colSpan: 2, styles: { halign: 'center' } },
      { content: 'Referee 2', colSpan: 2, styles: { halign: 'center' } },
    ]],
    body: [
      ['Name',        fmt(application.referee1_name),        'Name',        fmt(application.referee2_name)],
      ['Title',       fmt(application.referee1_title),       'Title',       fmt(application.referee2_title)],
      ['Institution', fmt(application.referee1_institution), 'Institution', fmt(application.referee2_institution)],
      ['Email',       fmt(application.referee1_email),       'Email',       fmt(application.referee2_email)],
      ['Telephone',   fmt(application.referee1_telephone),   'Telephone',   fmt(application.referee2_telephone)],
    ],
    theme: 'plain',
    headStyles: { fillColor: GREY700, textColor: WHITE, fontSize: 8.5, fontStyle: 'bold', cellPadding: 3 },
    styles: {
      fontSize: 8.5,
      cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 },
      textColor: GREY900,
      lineColor: GREY200,
      lineWidth: 0.2,
    },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: labelW, fillColor: GREY50, textColor: GREY700 },
      1: { cellWidth: valW },
      2: { fontStyle: 'bold', cellWidth: labelW, fillColor: GREY50, textColor: GREY700 },
      3: { cellWidth: valW },
    },
    rowPageBreak: 'avoid',
  });
  y = doc.lastAutoTable.finalY + 5;

  // ══════════════════════════════════════════════════════════════════════════
  //  7. DECLARATIONS & SIGNATURE
  // ══════════════════════════════════════════════════════════════════════════
  sectionHeading('7. Declarations & Undertakings');

  // Build declaration rows — prefer dynamic responses, fall back to legacy fields
  const declRows = [];
  if (Object.keys(dynDecl).length > 0) {
    // Dynamic declarations stored as { [id]: true/false }
    const labelMap = {
      info_true:       'Information provided is true, complete and accurate',
      competitive:     'Acknowledges the competitive nature of the programme',
      aptitude_tests:  'Willing to undertake aptitude tests and assessments',
      terms_agreed:    'Agreed to the Terms and Conditions',
    };
    Object.entries(dynDecl).forEach(([id, val]) => {
      const label = labelMap[id] || id;
      declRows.push([label, val ? 'YES' : 'NO']);
    });
  } else {
    // Fallback to top-level legacy fields
    declRows.push(['Information provided is true, complete and accurate', application.declaration_info_true     ? 'YES' : 'NO']);
    declRows.push(['Acknowledges the competitive nature of the programme', application.declaration_competitive    ? 'YES' : 'NO']);
    declRows.push(['Willing to undertake aptitude tests and assessments',  application.declaration_aptitude_tests ? 'YES' : 'NO']);
    declRows.push(['Agreed to the Terms and Conditions',                   application.declaration_terms_agreed   ? 'YES' : 'NO']);
  }
  declRows.push(['Declaration Date', fmt(application.declaration_date)]);
  dataTable(declRows);

  // Signature
  if (application.declaration_signature) {
    if (y + 44 > pageH - 22) { doc.addPage(); y = 20; }

    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...GREY700);
    doc.text('Applicant Signature:', mL, y + 4);

    // Bordered signature box
    doc.setFillColor(...WHITE);
    doc.setDrawColor(...GREY200);
    doc.setLineWidth(0.4);
    doc.roundedRect(mL + 54, y, 82, 36, 1.5, 1.5, 'FD');

    try {
      let sigData = application.declaration_signature;

      // If stored as a disk-path key, fetch it as a data URL first
      if (isFileKey(sigData)) {
        const objUrl = await fetchFileAsObjectUrl(sigData);
        if (objUrl) {
          // Convert object URL → base64 via canvas
          const img = new Image();
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = objUrl;
          });
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          canvas.getContext('2d').drawImage(img, 0, 0);
          sigData = canvas.toDataURL('image/png');
          URL.revokeObjectURL(objUrl);
        }
      }

      const fmt_sig = sigData.includes('data:image/jpeg') || sigData.includes('data:image/jpg')
        ? 'JPEG' : 'PNG';
      doc.addImage(sigData, fmt_sig, mL + 56, y + 2, 78, 32);
    } catch (e) {
      console.warn('Signature embed failed:', e);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(...GREY500);
      doc.text('(Signature could not be embedded)', mL + 95, y + 18, { align: 'center' });
    }
    y += 42;
  }

  // ══════════════════════════════════════════════════════════════════════════
  //  8. SUBMITTED DOCUMENTS
  // ══════════════════════════════════════════════════════════════════════════
  sectionHeading('8. Submitted Documents');
  dataTable([
    ['NIDA / Passport Copy',       application.nida_passport_copy     ? `Uploaded — ${application.nida_passport_copy_filename     || 'file'}` : 'Not uploaded'],
    ['O-Level Certificate',        application.additional_certificate ? `Uploaded — ${application.additional_certificate_filename || 'file'}` : 'Not uploaded'],
    ['A-Level Certificate',        application.a_level_certificate    ? `Uploaded — ${application.a_level_certificate_filename    || 'file'}` : 'Not uploaded'],
    ['LLB Certificate/Transcript', application.llb_certificate        ? `Uploaded — ${application.llb_certificate_filename        || 'file'}` : 'Not uploaded'],
    ['Declaration Signature',      application.declaration_signature  ? 'Uploaded' : 'Not uploaded'],
  ]);

  // ── Page numbers ───────────────────────────────────────────────────────────
  addPageNumbers();

  // ── Save ──────────────────────────────────────────────────────────────────
  const safeName = (application.full_name || 'Applicant')
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .replace(/\s+/g, '_');
  doc.save(`Application_${safeName}_${application.id.substring(0, 8).toUpperCase()}.pdf`);
  return true;
};
